import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router';
import styled from 'styled-components';
import { Button } from '@components/Common';
import OperationLoader from '@components/Common/Loaders/OperationLoader';

//#region styled
const CenterWrapper = styled.div`
  text-align: center;
`;

const ModeContainer = styled.div`
  display: inline-block;
  margin-bottom: 15px;
  background: rgb(51, 63, 72);
`;

const ModeLink = styled(NavLink)`
  display: inline-block;
  border: 1px solid #666;
  padding: 10px 35px;
  text-decoration: none;

  border-right-width: 0;
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.45);

  &:nth-child(4) {
    border-right-width: 1px;
  }

  &.active {
    background: none;
    color: rgba(255, 255, 255, 0.85);
  }

  &:hover {
    color: rgba(255, 255, 255, 0.85);
    background: rgba(255, 255, 255, 0.07);
  }
`;

const UpdateButton = styled(Button)`
  display: inline-block;
  padding: 10px 35px;
  text-decoration: none;

  border-right-width: 0;
  background: rgb(5, 195, 222);
  color: rgba(255, 255, 255);

  margin-left: 20px;
  border-right-width: 1px;
`;

const Loader = styled.div`
  display: inline-block;
  position: absolute;
  margin-top: 12px;
  margin-left: 10px;
`;

//#endregion

interface Props {
  fetch: () => void;
  isEnabled: boolean;
  isLoading: boolean;
}

/**
 * Build URL with tank/terminal filters preserved from current location.
 * This allows navigating between tabs while keeping the selected tank.
 */
const buildUrlWithFilters = (basePath: string, currentSearch: string): string => {
  const currentParams = new URLSearchParams(currentSearch);
  const newParams = new URLSearchParams();

  // Preserve tank and terminal filters
  const tank = currentParams.get('tank');
  const terminal = currentParams.get('terminal');
  if (tank) newParams.set('tank', tank);
  if (terminal) newParams.set('terminal', terminal);

  const queryString = newParams.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

const Modes: React.FC<Props> = ({ fetch, isEnabled, isLoading }) => {
  const { t } = useTranslation();
  const location = useLocation();

  // Build URLs with preserved tank/terminal filters
  const urls = useMemo(() => ({
    bookkeeping: buildUrlWithFilters('/bookkeeping', location.search),
    billing: buildUrlWithFilters('/bookkeeping/billing', location.search),
    approval: buildUrlWithFilters('/bookkeeping/approval', location.search),
    measurements: buildUrlWithFilters('/bookkeeping/measurements', location.search),
  }), [location.search]);

  return (
    <CenterWrapper>
      <ModeContainer>
        <ModeLink to={urls.bookkeeping} end>
          {t('pages.bookkeeping_entries')}
        </ModeLink>
        <ModeLink to={urls.billing}>
          {t('pages.billing')}
        </ModeLink>
        <ModeLink to={urls.approval}>
          {t('pages.approval')}
        </ModeLink>
        <ModeLink to={urls.measurements}>
          {t('pages.measurements')}
        </ModeLink>
      </ModeContainer>
      <UpdateButton
        primary={true}
        disabled={!isEnabled}
        onClick={() => {
          if (isEnabled) {
            fetch();
          }
        }}
      >
        {t('refresh_bookkeepings')}
      </UpdateButton>
      {isLoading && (
        <Loader>
          <OperationLoader title={t('updating') + '...'} />
        </Loader>
      )}
    </CenterWrapper>
  );
};

export default Modes;
